/**
 * shouldShowQuestionnaire=1 时写入 sessionStorage，
 * 刷新后由 App 消费并弹出调研问卷（避开考试页）。
 * 来源：登录接口 data / redirect.html 与路由 query。
 */
import { getBloomSurveyHost } from './host'

const STORAGE_KEY = 'bloom:shouldShowQuestionnaire'

/**
 * 考试相关路由（PRD：考试页不弹问卷）
 * 同时匹配 path / name / hash，避免仅 name=waitingExamination 时因缺少前导 / 漏判
 */
const EXAM_ROUTE_RE = /ExamSystem|waitingExamination|bloomEquipment|(^|\/|#)proctor|SuperQuiz|BattleRoom|BattleList|BattleResult|ExamIndex|ExamPaper|ExamEnd|ExercisePaper|AssessmentPaper/i

/** tryShow 已调度打开、但 dialog.show 尚未置 true 的短暂窗口 */
let bloomSurveyOpening = false

/**
 * @param {object|number|string} source
 *  - 登录/用户信息对象：读 source.shouldShowQuestionnaire
 *  - 路由 query：读 source.shouldShowQuestionnaire
 *  - 直接传 1 / '1'
 */
export function markBloomSurveyPending (source) {
  if (source == null) return
  const flag = typeof source === 'object'
    ? source.shouldShowQuestionnaire
    : source
  if (Number(flag) === 1) {
    sessionStorage.setItem(STORAGE_KEY, '1')
  }
}

export function hasBloomSurveyPending () {
  return sessionStorage.getItem(STORAGE_KEY) === '1'
}

export function clearBloomSurveyPending () {
  sessionStorage.removeItem(STORAGE_KEY)
}

export function markBloomSurveyOpening () {
  bloomSurveyOpening = true
}

export function clearBloomSurveyOpening () {
  bloomSurveyOpening = false
}

export function isBloomSurveyVisible () {
  try {
    const host = getBloomSurveyHost()
    if (!host) return false
    if (host.innerVisible != null) return !!host.innerVisible
    return !!(host.show || host.visible)
  } catch (e) {
    return false
  }
}

/** 待弹 / 打开中 / 已展示：活动提醒跳转应延后 */
export function isBloomSurveyBlocking () {
  return hasBloomSurveyPending() || bloomSurveyOpening || isBloomSurveyVisible()
}

/**
 * 调研弹窗关闭后再执行（无弹窗则尽快执行）。
 * graceMs：给 App tryShow 把 pending 转成 opening/visible 的时间。
 */
export function runAfterBloomSurveyClosed (callback, options) {
  if (typeof callback !== 'function') return
  const opts = options || {}
  const graceMs = opts.graceMs != null ? opts.graceMs : 100
  const intervalMs = opts.intervalMs != null ? opts.intervalMs : 120
  const maxWaitMs = opts.maxWaitMs != null ? opts.maxWaitMs : 10 * 60 * 1000
  const start = Date.now()

  const tick = () => {
    if (!isBloomSurveyBlocking()) {
      callback()
      return
    }
    if (Date.now() - start > maxWaitMs) {
      callback()
      return
    }
    setTimeout(tick, intervalMs)
  }

  setTimeout(tick, graceMs)
}

export function isExamRoute (route) {
  const parts = []
  if (route) {
    parts.push(route.path, route.name, route.fullPath)
  }
  if (typeof window !== 'undefined') {
    parts.push(window.location.hash, window.location.href)
  }
  return EXAM_ROUTE_RE.test(parts.filter(Boolean).join(' '))
}

/**
 * 若当前在考试页且问卷已打开：关闭并恢复待弹标记（离开考试后再弹）
 */
export function suppressBloomSurveyOnExamRoute (vm) {
  if (!vm || !isExamRoute(vm.$route)) return false
  try {
    const host = vm.$refs && vm.$refs.bloomSurveyDialog
    if (host && (host.show || host.innerVisible)) {
      if (typeof host.hide === 'function') host.hide()
      sessionStorage.setItem(STORAGE_KEY, '1')
    } else if (typeof vm.$_BloomSurveyDialog === 'function') {
      vm.$_BloomSurveyDialog(false)
    }
  } catch (e) {}
  return true
}

function isStudentUser (vm) {
  let userData = null
  try {
    userData = JSON.parse(vm.$utils.cache('userData'))
  } catch (e) {
    userData = null
  }
  return !!(userData && Number(userData.userType) === 0)
}

/**
 * 消费待弹标记并打开调研弹窗。
 * 考试页保留标记，离开考试页后再弹；非学生直接清标记。
 * 打开前在 nextTick 再检一次，避免登录 reload 后路由尚未稳定时误弹。
 */
export function tryShowBloomSurveyDialog (vm) {
  if (!vm || !hasBloomSurveyPending()) return false
  if (isExamRoute(vm.$route)) return false

  if (!isStudentUser(vm)) {
    clearBloomSurveyPending()
    return false
  }

  markBloomSurveyOpening()
  const open = () => {
    if (!hasBloomSurveyPending()) {
      clearBloomSurveyOpening()
      return
    }
    if (isExamRoute(vm.$route)) {
      clearBloomSurveyOpening()
      return
    }
    if (!isStudentUser(vm)) {
      clearBloomSurveyPending()
      clearBloomSurveyOpening()
      return
    }
    if (typeof vm.$_BloomSurveyDialog !== 'function') {
      clearBloomSurveyOpening()
      return
    }
    clearBloomSurveyPending()
    vm.$_BloomSurveyDialog()
    // 兜底：若 open 未回调，避免 opening 一直卡住（visible 仍会挡住跳转）
    setTimeout(() => {
      clearBloomSurveyOpening()
    }, 500)
  }

  if (typeof vm.$nextTick === 'function') {
    vm.$nextTick(open)
  } else {
    setTimeout(open, 0)
  }
  return true
}



// WEBPACK FOOTER //
// ./src/plugin/BloomSurveyDialog/pending.js